import React, { useState, useEffect, useRef } from 'react';
import { Clock, CheckCircle2, XCircle, FileText } from 'lucide-react';

const PASS_THRESHOLD = 0.7;

export const ExamFinal = ({ questions = [], initialResult = null, onFinish, onRetry }) => {
  const [phase, setPhase] = useState(initialResult ? 'result' : 'intro');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [localResult, setLocalResult] = useState(null); // resultado del intento actual
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const totalQuestions = questions.length;
  const answered = Object.keys(answers).filter((k) => answers[k] != null).length;

  const start = () => {
    setSecondsLeft(totalQuestions * 60);
    setPhase('running');
  };

  useEffect(() => {
    if (phase !== 'running' || secondsLeft === null) return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          submit();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const choose = (optionIndex) => {
    setAnswers((prev) => ({ ...prev, [questions[current].id]: optionIndex }));
  };

  const next = () => {
    if (current < totalQuestions - 1) setCurrent((c) => c + 1);
    else submit();
  };

  const submit = () => {
    clearInterval(timerRef.current);
    const correct = questions.filter((q, i) => answers[q.id] === q.correctIndex).length;
    const score = totalQuestions ? Math.round((correct / totalQuestions) * 100) : 0;
    const passed = score / 100 >= PASS_THRESHOLD;
    const result = { score, passed, correct, total: totalQuestions, answered: Object.keys(answers).filter((k) => answers[k] != null).length };
    setLocalResult(result);
    setPhase('result');
    if (onFinish) onFinish(result);
  };

  // Al reintentar se limpia el resultado local y se vuelve al intro.
  const retry = () => {
    setLocalResult(null);
    setAnswers({});
    setCurrent(0);
    setPhase('intro');
    if (onRetry) onRetry();
  };

  if (phase === 'intro') {
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem 0', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(197,160,89,0.15)', border: '1px solid rgba(197,160,89,0.35)',
        }}>
          <FileText className="w-6 h-6" style={{ color: 'var(--gold-matte)' }} />
        </div>
        <h4 style={{ color: '#fff', margin: 0 }}>Examen Final</h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', maxWidth: '420px', margin: 0, lineHeight: 1.6 }}>
          {totalQuestions} preguntas sobre todas las lecciones del curso. Tienes 1 minuto por pregunta.
          Debes acertar al menos el 70% para aprobar y poder descargar tu certificado.
        </p>
        <button
          onClick={start}
          style={{
            background: 'linear-gradient(135deg, #c5a059, #8f6f2f)', color: '#0a1a2a', border: 'none',
            borderRadius: '10px', padding: '12px 28px', fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer',
          }}
        >
          Comenzar examen
        </button>
      </div>
    );
  }

  if (phase === 'result') {
    const r = localResult || initialResult || {};
    const score = r.score != null ? r.score : 0;
    const passed = r.passed != null ? r.passed : false;
    const correct = r.correct != null ? r.correct : 0;
    const total = r.total != null ? r.total : totalQuestions;
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem 0', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: passed ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
          border: `2px solid ${passed ? '#10b981' : '#ef4444'}`,
        }}>
          {passed ? (
            <CheckCircle2 className="w-9 h-9" style={{ color: '#10b981' }} />
          ) : (
            <XCircle className="w-9 h-9" style={{ color: '#ef4444' }} />
          )}
        </div>
        <h4 style={{ color: '#fff', margin: 0 }}>{passed ? '¡Examen aprobado!' : 'No alcanzaste el mínimo'}</h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
          Calificación: <strong style={{ color: passed ? '#10b981' : '#ef4444', fontSize: '1.3rem' }}>{score}%</strong>
          <br />
          Correctas: {correct} de {total} · Umbral mínimo: 70%
        </p>
        {passed ? (
          <p style={{ fontSize: '0.8rem', color: '#a7f3d0', margin: 0 }}>
            Puedes descargar tu certificado de este tema desde la tarjeta "Certificados por Tema" en el panel lateral.
          </p>
        ) : (
          <button
            onClick={retry}
            style={{
              background: 'rgba(197,160,89,0.2)', color: '#c5a059', border: '1px solid rgba(197,160,89,0.4)',
              borderRadius: '10px', padding: '10px 22px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer',
            }}
          >
            Reintentar examen
          </button>
        )}
      </div>
    );
  }

  const q = questions[current];
  if (!q) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const selected = answers[q.id];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
          Pregunta {current + 1} de {totalQuestions}
        </span>
        <span style={{
          display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontFamily: 'monospace',
          color: secondsLeft < 60 ? '#ef4444' : 'var(--text-secondary)',
        }}>
          <Clock className="w-4 h-4" />
          {mins}:{secs}
        </span>
      </div>

      <div style={{
        padding: '1.25rem', borderRadius: '12px', background: 'rgba(10,26,42,0.6)',
        border: '1px solid rgba(197,160,89,0.25)',
      }}>
        <p style={{ color: '#fff', fontSize: '0.9rem', margin: 0, lineHeight: 1.6 }}>{q.question}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {q.options.map((opt, i) => {
          const isSelected = selected === i;
          return (
            <button
              key={i}
              onClick={() => choose(i)}
              style={{
                textAlign: 'left', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                fontSize: '0.82rem', lineHeight: 1.5, border: '1px solid',
                background: isSelected ? 'rgba(197,160,89,0.15)' : 'rgba(10,26,42,0.5)',
                borderColor: isSelected ? 'rgba(197,160,89,0.6)' : 'rgba(197,160,89,0.15)',
                color: isSelected ? '#c5a059' : 'var(--text-secondary)',
              }}
            >
              <span style={{ fontFamily: 'monospace', marginRight: '8px', opacity: 0.6 }}>{String.fromCharCode(65 + i)}</span>
              {opt}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          Respondidas: {answered} de {totalQuestions}
        </span>
        <button
          onClick={next}
          disabled={selected == null}
          style={{
            background: selected == null ? 'rgba(197,160,89,0.2)' : 'linear-gradient(135deg, #c5a059, #8f6f2f)',
            color: selected == null ? 'var(--text-muted)' : '#0a1a2a',
            border: 'none', borderRadius: '10px', padding: '10px 26px', fontSize: '0.82rem', fontWeight: 'bold',
            cursor: selected == null ? 'not-allowed' : 'pointer',
          }}
        >
          {current >= totalQuestions - 1 ? 'Finalizar examen' : 'Siguiente'}
        </button>
      </div>
    </div>
  );
};
